/**
 * Theme settings schema and shared theme helpers. Homepage sections live in
 * ./sections (one folder each); built-in themes live in the Laravel app's
 * resources/themes folder.
 */

import { SECTION_DEFINITIONS } from "./sections/definitions";
import { PAGE_TEMPLATE_SETTINGS } from "./templates/registry";
import { colorFields, textFields, PRODUCT_COLORS, PRODUCT_TEXTS, CATEGORY_COLORS, CATEGORY_TEXTS, COLLECTIONS_COLORS, COLLECTIONS_TEXTS } from "./pageCustomization";
import { PRODUCT_LAYOUT_FIELDS, PRODUCT_PAGE_SECTIONS_FIELD, PRODUCT_TEXT_FIELDS } from "./productPageSettings";

// Built-in themes (settings, sections, and library card text) live in
// ecommerce-laravel/resources/themes/*.json, one file per theme.

export { createReview, createHeroSlide, resolveHeroSlides, withColorFields } from "./sectionKit";
export { SECTION_DEFINITIONS };


export function themeButtonRadius(buttonStyle) {
  if (buttonStyle === "pill") return "9999px";
  if (buttonStyle === "sharp") return "4px";
  return "8px";
}

/** Storefront buttons follow the theme color and shape unless a section overrides them. */
export function themeButtonAppearance(globalSettings = {}) {
  return {
    backgroundColor: globalSettings.buttonColor || globalSettings.primaryColor || "#111827",
    color: globalSettings.buttonTextColor || "#ffffff",
    borderRadius: themeButtonRadius(globalSettings.buttonStyle),
    border: "none",
  };
}

function paint(value, fallback) {
  if (value == null) return fallback;
  const text = String(value).trim();
  return text ? text : fallback;
}

/** A section color wins. An empty section color keeps the theme color. */
export function sectionPalette(settings = {}, globalSettings = {}) {
  return {
    background: paint(settings.backgroundColor, paint(globalSettings.backgroundColor, "#ffffff")),
    text: paint(settings.textColor, paint(globalSettings.textColor, "#111827")),
    muted: paint(settings.mutedTextColor, paint(globalSettings.mutedTextColor, "#6b655c")),
    accent: paint(settings.accentColor, paint(globalSettings.accentColor, "#2563eb")),
    surface: paint(settings.surfaceColor, paint(globalSettings.surfaceColor, "#ffffff")),
  };
}

export function sectionButton(settings = {}, globalSettings = {}) {
  return themeButtonAppearance({
    ...globalSettings,
    buttonColor: paint(settings.buttonColor, globalSettings.buttonColor),
    buttonTextColor: paint(settings.buttonTextColor, globalSettings.buttonTextColor),
  });
}

export function themeButtonCssVars(globalSettings = {}) {
  const button = themeButtonAppearance(globalSettings);
  return {
    "--theme-button": button.backgroundColor,
    "--theme-button-text": button.color,
    "--theme-button-radius": button.borderRadius,
    "--theme-primary": globalSettings.primaryColor || "#111827",
    "--theme-accent": globalSettings.accentColor || "#2563eb",
  };
}

/**
 * Theme settings groups. `id` is how the editor finds a group (never its
 * position), so groups can be added or reordered safely. `title` and `hint`
 * label it in the editor's Theme tab.
 */
export const GLOBAL_SETTINGS_SCHEMA = [
  {
    id: "colors",
    title: "Colors",
    hint: "Brand, surfaces, and text",
    category: "Theme Colors & Palette",
    fields: [
      { key: "primaryColor", label: "Primary Theme Color", type: "color" },
      { key: "secondaryColor", label: "Secondary / Dark Color", type: "color" },
      { key: "accentColor", label: "Accent / Highlight Color", type: "color" },
      { key: "backgroundColor", label: "Page Canvas Background", type: "color" },
      { key: "surfaceColor", label: "Card & Surface Background", type: "color" },
      { key: "textColor", label: "Primary Text Color", type: "color" },
      { key: "mutedTextColor", label: "Secondary text color", type: "color", fallback: "#6B655C" },
    ],
  },
  {
    id: "typography",
    title: "Typography",
    hint: "Type and layout",
    category: "Typography & Layout",
    fields: [
      {
        key: "fontFamily",
        label: "Heading Font Family",
        type: "select",
        options: [
          { value: "'Cormorant Garamond', Georgia, serif", label: "Cormorant Garamond (Luxury serif)" },
          { value: "'Playfair Display', Georgia, serif", label: "Playfair Display (Editorial / Luxury)" },
          { value: "'Rajdhani', -apple-system, BlinkMacSystemFont, sans-serif", label: "Rajdhani (Tech / Modern)" },
          { value: "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, sans-serif", label: "Plus Jakarta Sans (Friendly / Universal)" },
          { value: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif", label: "Inter (Clean & Minimal)" },
          { value: "'Outfit', -apple-system, BlinkMacSystemFont, sans-serif", label: "Outfit (Modern Geometric)" },
          { value: "'Bricolage Grotesque', 'Plus Jakarta Sans', sans-serif", label: "Bricolage Grotesque (Showcase)" },
          { value: "'DM Sans', 'Inter', sans-serif", label: "DM Sans (Body)" },
        ],
      },
      {
        key: "bodyFontFamily",
        label: "Body font",
        type: "select",
        options: [
          { value: "'Jost', 'Inter', sans-serif", label: "Jost" },
          { value: "'DM Sans', 'Inter', sans-serif", label: "DM Sans" },
          { value: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif", label: "Inter" },
          { value: "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, sans-serif", label: "Plus Jakarta Sans" },
        ],
      },
      {
        key: "productCardStyle",
        label: "Product Card Style",
        type: "select",
        options: [
          { value: "prestige", label: "Renown (portrait photo, quiet type, no card frame)" },
          { value: "editorial", label: "Editorial Luxury (Portrait, subtle zoom, clean borders)" },
          { value: "tech", label: "High-Tech Retail (Specs chips, discount pill, high contrast)" },
          { value: "showcase", label: "Showcase cards (soft photo, price, add button)" },
          { value: "standard", label: "Universal Superstore (Badges, ratings, fast add-to-cart)" },
        ],
      },
      {
        key: "productImageRatio",
        label: "Default Product Image Ratio",
        type: "select",
        options: [
          { value: "portrait", label: "Portrait 3:4 (Fashion)" },
          { value: "square", label: "Square 1:1 (Universal & Electronics)" },
        ],
      },
      {
        key: "pageWidth",
        label: "Max Container Width",
        type: "select",
        options: [
          { value: "1200px", label: "Compact (1200px)" },
          { value: "1280px", label: "Standard (1280px)" },
          { value: "1360px", label: "Wide (1360px)" },
          { value: "1440px", label: "Ultra-Wide (1440px)" },
        ],
      },
    ],
  },
  {
    id: "buttons",
    title: "Buttons",
    hint: "Color, text, and shape",
    category: "Buttons",
    fields: [
      {
        key: "buttonStyle",
        label: "Button style",
        type: "select",
        options: [
          { value: "pill", label: "Pill" },
          { value: "rounded", label: "Rounded" },
          { value: "sharp", label: "Sharp" },
        ],
      },
      { key: "buttonColor", label: "Button color", type: "color", fallback: "#111827" },
      { key: "buttonTextColor", label: "Button text color", type: "color", fallback: "#ffffff" },
    ],
  },
  {
    id: "header",
    title: "Header",
    hint: "Announcement, search, menus, and links",
    category: "Announcement & Header",
    fields: [
      {
        key: "headerLayout",
        label: "Header",
        type: "choice-cards",
        options: [
          { value: "classic", label: "Current", hint: "Logo, search bar, and menu" },
          { value: "centered", label: "Centered menu", hint: "Logo left, menu center, icons right" },
          { value: "logo-center", label: "Logo center", hint: "Logo centered, menu underneath" },
          { value: "prestige", label: "Renown", hint: "Menu on the left, name in the center, icons on the right" },
        ],
      },
      { key: "headerBackground", label: "Header background", type: "color", fallback: "#0a0a0a" },
      { key: "headerTextColor", label: "Header text and icons", type: "color", fallback: "#ffffff" },
      { key: "searchBackground", label: "Search panel background", type: "color", fallback: "#111111" },
      { key: "searchTextColor", label: "Search heading color", type: "color", fallback: "#ffffff" },
      { key: "searchOverlay", label: "Page dim", type: "color", fallback: "#000000" },
      { key: "searchInputBackground", label: "Search box background", type: "color", fallback: "#ffffff" },
      { key: "searchInputText", label: "Search box text", type: "color", fallback: "#111827" },
      { key: "searchCardBackground", label: "Product card background", type: "color", fallback: "#ffffff" },
      { key: "searchNameColor", label: "Product name", type: "color", fallback: "#111827" },
      { key: "searchPriceColor", label: "Product price", type: "color", fallback: "#6b7280" },
      { key: "searchPlaceholder", label: "Search placeholder", type: "text", placeholder: "Search" },
      { key: "searchRecentLabel", label: "Recently viewed heading", type: "text", placeholder: "Recently viewed" },
      { key: "searchProductsLabel", label: "Products heading", type: "text", placeholder: "Products" },
      { key: "searchClearLabel", label: "Clear wording", type: "text", placeholder: "Clear" },
      { key: "showAnnouncementBar", label: "Enable Top Announcement Bar", type: "toggle" },
      { key: "announcementBarText", label: "Announcement Message", type: "text" },
      { key: "announcementBarBg", label: "Announcement Background Color", type: "color" },
      { key: "announcementBarColor", label: "Announcement Text Color", type: "color" },
      { key: "enableStickyHeader", label: "Sticky Navigation on Scroll", type: "toggle" },
    ],
  },
  {
    id: "footer",
    title: "Footer",
    hint: "Colors, brand, menus, and copyright",
    category: "Footer & Legal",
    fields: [
      { key: "footerBackground", label: "Background", type: "color", fallback: "#0f172a" },
      { key: "footerHeadingColor", label: "Heading color", type: "color", fallback: "#f8fafc" },
      { key: "footerTextColor", label: "Text and link color", type: "color", fallback: "#94a3b8" },
      { key: "footerTagline", label: "Store description", type: "textarea" },
      { key: "showFooterLogo", label: "Show logo", type: "toggle" },
      { key: "showFooterSocial", label: "Show social icons", type: "toggle" },
      { key: "showFooterContact", label: "Show contact details", type: "toggle" },
      { key: "showNewsletterInFooter", label: "Show newsletter", type: "toggle", defaultOn: false },
      { key: "footerNewsletterTitle", label: "Newsletter heading", type: "text" },
      { key: "footerCopyright", label: "Copyright line", type: "text" },
    ],
  },
  {
    id: "side-cart",
    title: "Side cart",
    hint: "Slide-out cart, wording, and links",
    category: "Side cart",
    fields: [
      { key: "cartShowRecommendations", label: "Show product suggestions in the cart", type: "toggle" },
      { key: "cartRecommendationsTitle", label: "Suggestions heading", type: "text", placeholder: "You may also like" },
      {
        key: "cartSide",
        label: "Which side it opens from",
        type: "select",
        options: [
          { value: "right", label: "Right side" },
          { value: "left", label: "Left side" },
        ],
      },
      { key: "cartBackground", label: "Panel background", type: "color", fallback: "#ffffff" },
      { key: "cartHeadingColor", label: "Heading color", type: "color", fallback: "#111827" },
      { key: "cartTextColor", label: "Small text color", type: "color", fallback: "#6b7280" },
      { key: "cartIconColor", label: "Icon color", type: "color", fallback: "#2563eb" },
      { key: "cartItemBackground", label: "Item background", type: "color", fallback: "#ffffff" },
      { key: "cartItemBorder", label: "Item outline", type: "color", fallback: "#e5e7eb" },
      { key: "cartItemNameColor", label: "Item name", type: "color", fallback: "#111827" },
      { key: "cartItemPriceColor", label: "Item price", type: "color", fallback: "#2563eb" },
      { key: "cartItemOldPriceColor", label: "Old price", type: "color", fallback: "#9ca3af" },
      { key: "cartRemoveColor", label: "Remove icon", type: "color", fallback: "#dc2626" },
      { key: "cartQtyBackground", label: "Quantity background", type: "color", fallback: "#f3f4f6" },
      { key: "cartQtyTextColor", label: "Quantity text", type: "color", fallback: "#111827" },
      { key: "cartSubtotalLabel", label: "Subtotal words", type: "text", placeholder: "Subtotal" },
      { key: "cartSubtotalLabelColor", label: "Subtotal words color", type: "color", fallback: "#6b7280" },
      { key: "cartSubtotalColor", label: "Subtotal amount color", type: "color", fallback: "#111827" },
      { key: "cartSavingsLabel", label: "Savings words", type: "text", placeholder: "Discount Savings" },
      { key: "cartSavingsColor", label: "Savings color", type: "color", fallback: "#15803d" },
      { key: "cartShippingBackground", label: "Shipping bar background", type: "color", fallback: "#f8fafc" },
      { key: "cartShippingTextColor", label: "Shipping text", type: "color", fallback: "#111827" },
      { key: "cartShippingFreeColor", label: "Free delivery text", type: "color", fallback: "#15803d" },
      { key: "cartShippingBarColor", label: "Shipping progress", type: "color", fallback: "#16a34a" },
      { key: "cartShippingFree", label: "Free delivery message", type: "text", placeholder: "FREE Delivery on All Orders!" },
      { key: "cartShippingUnlocked", label: "Unlocked delivery message", type: "text", placeholder: "You unlocked FREE Nationwide Delivery!" },
      { key: "cartShippingAdd", label: "Amount still needed", type: "text", placeholder: "Add {amount} for Free Shipping" },
      { key: "cartShippingAddColor", label: "Add Rs. … text", type: "color", fallback: "#111827" },
      { key: "cartLinkColor", label: "Extra link color", type: "color", fallback: "#6b7280" },
      { key: "cartHeading", label: "Heading", type: "text", placeholder: "Your Cart" },
      { key: "cartEmptyTitle", label: "Empty cart title", type: "text", placeholder: "Your cart is empty" },
      { key: "cartEmptyMessage", label: "Empty cart message", type: "textarea", placeholder: "Add items from our store to start checkout." },
      { key: "cartEmptyButton", label: "Empty cart button", type: "text", placeholder: "Explore Collections" },
      { key: "cartCheckoutLabel", label: "Button words", type: "text", placeholder: "Checkout" },
      { key: "cartCheckoutColor", label: "Button color", type: "color", fallback: "#111827" },
      { key: "cartCheckoutTextColor", label: "Text color", type: "color", fallback: "#ffffff" },
      { key: "cartCheckoutBorderColor", label: "Outer color", type: "color", fallback: "#111827" },
      { key: "cartViewLabel", label: "Button words", type: "text", placeholder: "View Full Cart" },
      { key: "cartViewColor", label: "Button color", type: "color", fallback: "#ffffff" },
      { key: "cartViewTextColor", label: "Text color", type: "color", fallback: "#111827" },
      { key: "cartViewBorderColor", label: "Outer color", type: "color", fallback: "#111827" },
      { key: "cartNote", label: "Note under the total", type: "text", placeholder: "Taxes and shipping calculated at checkout" },
      { key: "cartShowShipping", label: "Free shipping bar", type: "toggle" },
      { key: "cartShowSavings", label: "Savings line", type: "toggle" },
      { key: "cartShowViewCart", label: "Show the full cart button", type: "toggle" },
    ],
  },
  {
    id: "collections-page",
    title: "Collections page",
    hint: "The page listing every category",
    category: "Collections page",
    fields: [
      { key: "collectionIndexBackground", label: "Page background", type: "color", hint: "Leave empty to match the Category page." },
      { key: "collectionIndexHeadingColor", label: "Title color", type: "color", hint: "Leave empty to match the Category page." },
      {
        key: "collectionIndexTitleAlign",
        label: "Title alignment",
        type: "select",
        options: [
          { value: "", label: "Same as the Category page" },
          { value: "left", label: "Left" },
          { value: "center", label: "Center" },
        ],
      },
      {
        key: "collectionIndexShowBreadcrumbs",
        label: "Breadcrumbs (Home / Collections)",
        type: "select",
        options: [
          { value: "", label: "Same as the Category page" },
          { value: "true", label: "Show" },
          { value: "false", label: "Hide" },
        ],
      },
      {
        key: "collectionIndexColumns",
        label: "Collections per row on desktop",
        type: "select",
        options: [
          { value: "3", label: "3" },
          { value: "2", label: "2" },
          { value: "4", label: "4" },
        ],
      },
      {
        key: "collectionIndexColumnsMobile",
        label: "Collections per row on phone",
        type: "select",
        options: [
          { value: "2", label: "2" },
          { value: "1", label: "1" },
        ],
      },
      { key: "collectionIndexShowCount", label: "Show product count on the card", type: "toggle" },
      { key: "collectionIndexCaption", label: "Collection name color", type: "color", fallback: "#ffffff" },
      { key: "collectionIndexOverlay", label: "Photo shade color", type: "color", fallback: "#000000" },
      {
        key: "collectionIndexPerPage",
        label: "Collections per page",
        type: "select",
        options: [
          { value: "", label: "Same as the Category page" },
          { value: "8", label: "8" },
          { value: "12", label: "12" },
          { value: "16", label: "16" },
          { value: "24", label: "24" },
        ],
      },
      {
        key: "collectionIndexPagination",
        label: "More collections",
        type: "select",
        options: [
          { value: "", label: "Same as the Category page" },
          { value: "pages", label: "Page numbers" },
          { value: "more", label: "Load more button" },
        ],
      },
      ...colorFields(COLLECTIONS_COLORS),
      ...textFields(COLLECTIONS_TEXTS),
    ],
  },
  {
    id: "collection-page",
    title: "Category page",
    hint: "One category with its products: grid, filters, and cards",
    category: "Category page",
    fields: [
      { key: "collectionBackground", label: "Page background", type: "color", fallback: "#ffffff" },
      { key: "collectionHeadingColor", label: "Heading color", type: "color", fallback: "#121212" },
      { key: "collectionTextColor", label: "Small text color", type: "color", fallback: "#444444" },
      {
        key: "collectionTitleAlign",
        label: "Title alignment",
        type: "select",
        options: [
          { value: "left", label: "Left" },
          { value: "center", label: "Center" },
        ],
      },
      {
        key: "collectionTitleSize",
        label: "Title size",
        type: "select",
        options: [
          { value: "medium", label: "Medium" },
          { value: "small", label: "Small" },
          { value: "large", label: "Large" },
        ],
      },
      { key: "collectionShowBreadcrumbs", label: "Show breadcrumbs", type: "toggle" },
      { key: "collectionShowDescription", label: "Show description", type: "toggle" },
      { key: "collectionShowCount", label: "Show product count", type: "toggle" },
      { key: "collectionShowFilter", label: "Show filter", type: "toggle" },
      { key: "collectionShowSort", label: "Show sort", type: "toggle" },
      {
        key: "collectionFilterStyle",
        label: "Filter style",
        type: "select",
        options: [
          { value: "drawer", label: "Side panel" },
          { value: "sidebar", label: "Left column" },
        ],
      },
      {
        key: "collectionPagination",
        label: "How more products load",
        type: "select",
        options: [
          { value: "pages", label: "Page numbers" },
          { value: "more", label: "Load more button" },
        ],
      },
      {
        key: "collectionPerPage",
        label: "Products per page",
        type: "select",
        options: [
          { value: "16", label: "16" },
          { value: "8", label: "8" },
          { value: "12", label: "12" },
          { value: "24", label: "24" },
        ],
      },
      {
        key: "collectionColumns",
        label: "Products per row on desktop",
        type: "select",
        options: [
          { value: "4", label: "4" },
          { value: "2", label: "2" },
          { value: "3", label: "3" },
          { value: "5", label: "5" },
        ],
      },
      {
        key: "collectionColumnsMobile",
        label: "Products per row on phone",
        type: "select",
        options: [
          { value: "2", label: "2" },
          { value: "1", label: "1" },
        ],
      },
      {
        key: "collectionImageRatio",
        label: "Product photo shape",
        type: "select",
        options: [
          { value: "portrait", label: "Portrait" },
          { value: "square", label: "Square" },
          { value: "landscape", label: "Landscape" },
        ],
      },
      {
        key: "collectionImageFit",
        label: "Product photo fit",
        type: "select",
        options: [
          { value: "cover", label: "Fill the shape (may crop edges)" },
          { value: "contain", label: "Show the whole photo" },
        ],
      },
      { key: "collectionNameColor", label: "Product name", type: "color", fallback: "#121212" },
      { key: "collectionPriceColor", label: "Price", type: "color", fallback: "#121212" },
      { key: "collectionOldPriceColor", label: "Old price", type: "color", fallback: "#6b7280" },
      { key: "collectionVendorColor", label: "Brand", type: "color", fallback: "#6b7280" },
      { key: "collectionShowVendor", label: "Show brand", type: "toggle" },
      { key: "collectionShowSwatches", label: "Show color dots", type: "toggle" },
      { key: "collectionShowHoverImage", label: "Second photo on hover", type: "toggle" },
      { key: "collectionShowWishlist", label: "Show wishlist heart", type: "toggle" },
      { key: "collectionShowSaleBadge", label: "Show sale badge", type: "toggle" },
      { key: "collectionSaleLabel", label: "Sale badge words", type: "text", placeholder: "Sale" },
      { key: "collectionSaleBackground", label: "Sale badge color", type: "color", fallback: "#ffffff" },
      { key: "collectionSaleText", label: "Sale badge text", type: "color", fallback: "#121212" },
      { key: "collectionShowSoldOut", label: "Show sold out badge", type: "toggle" },
      { key: "collectionSoldLabel", label: "Sold out words", type: "text", placeholder: "Sold out" },
      { key: "collectionSoldBackground", label: "Sold out badge color", type: "color", fallback: "#ffffff" },
      { key: "collectionSoldText", label: "Sold out badge text", type: "color", fallback: "#121212" },
      { key: "collectionShowQuickAdd", label: "Show quick add", type: "toggle" },
      { key: "collectionAddLabel", label: "Add to cart words", type: "text", placeholder: "Add to cart" },
      { key: "collectionChooseLabel", label: "Choose options words", type: "text", placeholder: "Choose options" },
      { key: "collectionQuickBackground", label: "Quick add color", type: "color", fallback: "#ffffff" },
      { key: "collectionQuickText", label: "Quick add text", type: "color", fallback: "#121212" },
      { key: "collectionQuickBorder", label: "Quick add outer color", type: "color", fallback: "#121212" },
      ...colorFields(CATEGORY_COLORS),
      ...textFields(CATEGORY_TEXTS),
    ],
  },
  {
    id: "product-page",
    title: "Product page",
    hint: "Photo size, layout, Buy Now, and sections below",
    category: "Product page",
    fields: [
      {
        key: "productMobileAlign",
        label: "Content alignment on a phone",
        type: "select",
        options: [
          { value: "center", label: "Center" },
          { value: "left", label: "Left" },
        ],
      },
      { key: "productMobileBuyFull", label: "Buy Now full width on a phone", type: "toggle" },
      ...PRODUCT_LAYOUT_FIELDS,
      ...PRODUCT_TEXT_FIELDS,
      ...colorFields(PRODUCT_COLORS),
      ...textFields(PRODUCT_TEXTS),
      PRODUCT_PAGE_SECTIONS_FIELD,
    ],
  },
  {
    id: "theme-style",
    title: "Theme style",
    hint: "Professional one-click design presets",
    category: "Theme styles",
    fields: [],
  },
  PAGE_TEMPLATE_SETTINGS,
];
